import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { StandardItemCatalogPicker } from "../components/StandardItemCatalogPicker";
import { useAppState } from "../context/AppStateContext";
import type { StandardResource, StandardResourceItem } from "../types";
import { sortStandardResources } from "../utils/standardResources";
import { parseQuantityInput } from "../utils/qtyInput";

function ItemEditorRow({ resourceId, item }: { resourceId: string; item: StandardResourceItem }) {
  const { updateStandardResourceItem, removeStandardResourceItem } = useAppState();
  const [label, setLabel] = useState(item.label);
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unit, setUnit] = useState(item.unit);
  const [rate, setRate] = useState(String(item.rate ?? 0));
  const [hsnCode, setHsnCode] = useState(item.hsnCode ?? "");

  useEffect(() => {
    setLabel(item.label);
    setQuantity(String(item.quantity));
    setUnit(item.unit);
    setRate(String(item.rate ?? 0));
    setHsnCode(item.hsnCode ?? "");
  }, [item.id, item.label, item.quantity, item.unit, item.rate, item.hsnCode]);

  return (
    <div className="standard-resource-item-row">
      {item.catalogStandardItemId ? (
        <p className="text-muted section-lead section-lead--tight" style={{ width: "100%", margin: "0 0 0.35rem" }}>
          Catalogue ID: <code>{item.catalogStandardItemId}</code>
        </p>
      ) : null}
      <div className="field" style={{ flex: "1 1 10rem", marginBottom: 0 }}>
        <label className="sr-only" htmlFor={`sri-label-${item.id}`}>
          Item description
        </label>
        <input
          id={`sri-label-${item.id}`}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. OPC bags"
          maxLength={200}
        />
      </div>
      <div className="field standard-resource-item-row__qty" style={{ marginBottom: 0 }}>
        <label className="sr-only" htmlFor={`sri-qty-${item.id}`}>
          Quantity
        </label>
        <input
          id={`sri-qty-${item.id}`}
          type="number"
          min={0}
          step={1}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />
      </div>
      <div className="field standard-resource-item-row__qty" style={{ marginBottom: 0, maxWidth: "7rem" }}>
        <label className="sr-only" htmlFor={`sri-rate-${item.id}`}>
          Rate (₹ per unit)
        </label>
        <input
          id={`sri-rate-${item.id}`}
          type="text"
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder="0"
          title="Unit rate in ₹ — used on PO / issue material when generated"
        />
      </div>
      <div className="field standard-resource-item-row__qty" style={{ marginBottom: 0, maxWidth: "8rem" }}>
        <label className="sr-only" htmlFor={`sri-hsn-${item.id}`}>
          HSN / SAC
        </label>
        <input
          id={`sri-hsn-${item.id}`}
          type="text"
          inputMode="numeric"
          value={hsnCode}
          onChange={(e) => setHsnCode(e.target.value)}
          placeholder="HSN"
          maxLength={40}
          title="HSN/SAC — copied to PO when generated"
          className="mono"
        />
      </div>
      <div className="field" style={{ flex: "1 1 6rem", marginBottom: 0 }}>
        <label className="sr-only" htmlFor={`sri-unit-${item.id}`}>
          Unit
        </label>
        <input
          id={`sri-unit-${item.id}`}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="Bag"
          maxLength={40}
        />
      </div>
      <div className="standard-resource-item-row__actions">
        <button
          type="button"
          className="btn btn--primary btn--tg btn--sm"
          disabled={!label.trim() || !unit.trim()}
          onClick={() => {
            const parsedRate = Math.max(0, parseQuantityInput(rate));
            const hsn = hsnCode.trim().replace(/\s+/g, "").slice(0, 40);
            void updateStandardResourceItem(resourceId, item.id, {
              label: label.trim(),
              quantity: Math.max(0, Math.floor(Number(quantity)) || 0),
              unit: unit.trim(),
              rate: parsedRate,
              hsnCode: hsn,
            });
          }}
        >
          Save line
        </button>
        <button
          type="button"
          className="btn btn--link btn--sm"
          onClick={() => {
            if (window.confirm("Remove this item line?")) void removeStandardResourceItem(resourceId, item.id);
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function StandardResourceSummaryCard({
  resource: r,
  resourceIndex,
  isActive,
  onOpen,
}: {
  resource: StandardResource;
  resourceIndex: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  const n = r.items.length;
  const note = r.notes.trim();
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Resource {resourceIndex + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{r.title}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Line count">
          <span>
            {n} item line{n === 1 ? "" : "s"}
          </span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function ResourceEditorForm({
  resource: r,
  index,
  total,
  onDeleted,
}: {
  resource: StandardResource;
  index: number;
  total: number;
  onDeleted?: () => void;
}) {
  const { updateStandardResource, removeStandardResource, moveStandardResource } = useAppState();
  const [title, setTitle] = useState(r.title);
  const [notes, setNotes] = useState(r.notes);

  useEffect(() => {
    setTitle(r.title);
    setNotes(r.notes);
  }, [r.id, r.title, r.notes]);

  return (
    <div className="milestone-master-modal-editor">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <p className="text-muted section-lead section-lead--tight" style={{ margin: 0, maxWidth: "36rem" }}>
          Resource {index + 1} of {total}. Order matches milestone resource pickers. Deleting removes links from milestone
          templates that reference this resource.
        </p>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm milestone-row__remove"
          onClick={() => {
            if (
              window.confirm(
                `Delete “${r.title}”? Milestones that link this resource will lose the link.`,
              )
            ) {
              void (async () => {
                const ok = await removeStandardResource(r.id);
                if (ok) onDeleted?.();
              })();
            }
          }}
        >
          Delete resource
        </button>
      </div>

      <div className="field">
        <label htmlFor={`sr-title-${r.id}`}>Resource name</label>
        <input id={`sr-title-${r.id}`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor={`sr-notes-${r.id}`}>Notes (optional)</label>
        <textarea id={`sr-notes-${r.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </div>
      <div className="toolbar-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn--primary btn--tg btn--sm"
          disabled={!title.trim()}
          onClick={() => void updateStandardResource(r.id, { title: title.trim(), notes })}
        >
          Save name &amp; notes
        </button>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm"
          disabled={index <= 0}
          onClick={() => void moveStandardResource(r.id, "up")}
        >
          Move up
        </button>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm"
          disabled={index >= total - 1}
          onClick={() => void moveStandardResource(r.id, "down")}
        >
          Move down
        </button>
      </div>

      <h3 className="form-section-title" style={{ marginTop: "1.15rem" }}>
        Item lines
      </h3>
      <p className="text-muted section-lead section-lead--tight">
        Add lines only from the standard item catalogue below (MySQL and imported master required). Milestones show these
        lines when this resource is linked under <Link to="/milestone-master">Milestone master</Link>.
      </p>

      <StandardItemCatalogPicker resource={r} />

      {r.items.length === 0 ? (
        <p className="text-muted section-lead section-lead--tight">
          No item lines yet — use the catalogue above after choosing a main group.
        </p>
      ) : (
        <div className="standard-resource-items">
          {r.items.map((it) => (
            <ItemEditorRow key={it.id} resourceId={r.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

export function StandardResourcesMaster() {
  const { standardResources, addStandardResource } = useAppState();
  const sorted = useMemo(() => sortStandardResources(standardResources), [standardResources]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalResourceId, setEditModalResourceId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const editModalResource = useMemo(
    () => (editModalResourceId ? sorted.find((x) => x.id === editModalResourceId) : undefined),
    [editModalResourceId, sorted],
  );

  const editModalIndex = useMemo(() => {
    if (!editModalResource) return -1;
    return sorted.findIndex((r) => r.id === editModalResource.id);
  }, [editModalResource, sorted]);

  useEffect(() => {
    if (editModalResourceId && !editModalResource) setEditModalResourceId(null);
  }, [editModalResourceId, editModalResource]);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setNewTitle("");
    setNewNotes("");
  }, []);

  useEffect(() => {
    if (!createModalOpen && !editModalResourceId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setEditModalResourceId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createModalOpen, editModalResourceId, closeCreateModal]);

  const openCreateModal = () => {
    setEditModalResourceId(null);
    setCreateModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setCreateModalOpen(false);
    setEditModalResourceId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="milestone-master-page">
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Standard resources" }]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Standard resources master
            <span className="milestone-master__catalog-count">{sorted.length}</span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={openCreateModal}>
            Add resource
          </button>
        }
      />

      <section
        className="milestone-master__catalog"
        aria-labelledby="standard-resources-catalog-heading"
        style={{ marginTop: "1.75rem" }}
      >

        {sorted.length === 0 ? (
          <div className="milestone-master__empty">
            <p className="milestone-master__empty-title">No resources yet</p>
            <p className="milestone-master__empty-desc">
              Create your first category with <strong>Add resource</strong>, then open the card to add catalogue item lines.
            </p>
          </div>
        ) : (
          <div className="milestone-master-picker-grid">
            {sorted.map((r, i) => (
              <StandardResourceSummaryCard
                key={r.id}
                resource={r}
                resourceIndex={i}
                isActive={editModalResourceId === r.id}
                onOpen={() => openEditModal(r.id)}
              />
            ))}
          </div>
        )}
      </section>

      {createModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            closeCreateModal();
          }}
        >
          <div
            className="modal milestone-master-modal standard-resources-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="standard-resources-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="standard-resources-create-title" className="milestone-master-modal__title">
                Add resource
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <div className="field">
                <label htmlFor="new-sr-title">Resource name</label>
                <input
                  id="new-sr-title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Cement"
                  maxLength={200}
                />
              </div>
              <div className="field">
                <label htmlFor="new-sr-notes">Notes (optional)</label>
                <textarea
                  id="new-sr-notes"
                  rows={2}
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  maxLength={2000}
                />
              </div>
              <p className="text-muted section-lead section-lead--tight" style={{ marginTop: "0.25rem" }}>
                After you create the resource, open its card to add item lines from the standard catalogue.
              </p>
              <div className="toolbar-row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
                <button
                  type="button"
                  className="btn btn--primary btn--tg"
                  disabled={!newTitle.trim()}
                  onClick={() => {
                    void (async () => {
                      const id = await addStandardResource({ title: newTitle.trim(), notes: newNotes.trim() });
                      if (!id) return;
                      closeCreateModal();
                    })();
                  }}
                >
                  Add resource
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editModalResource && editModalIndex >= 0 ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setEditModalResourceId(null);
          }}
        >
          <div
            className="modal milestone-master-modal standard-resources-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="standard-resources-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="standard-resources-edit-title" className="milestone-master-modal__title">
                Edit resource
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalResourceId(null)}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <ResourceEditorForm
                key={editModalResource.id}
                resource={editModalResource}
                index={editModalIndex}
                total={sorted.length}
                onDeleted={() => setEditModalResourceId(null)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
